import dayjs from "dayjs";
import { FC } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import type { ReactDatePickerProps } from "react-datepicker";
import it from "date-fns/locale/it"; // the locale you want
import en from "date-fns/locale/en-US"; //
import { MonthDatepicker } from "./monthDatePicker";
import isoWeek from "dayjs/plugin/isoWeek";
import {
  CalendarDotsIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from "@phosphor-icons/react";
import "react-datepicker/dist/react-datepicker.css";
import "./CustomDatePicker.scss";

// BUG mismatch tipi @types/react-datepicker (dipendenza transitiva su @types/react v19)
// vs @types/react v18 del progetto: `DatePicker` non è più assegnabile a JSX.ElementType.
// Wrapper funzionale tipizzato per bypassare l'incompatibilità di classe senza usare `any`.
const TypedDatePicker = DatePicker as unknown as (
  props: ReactDatePickerProps
) => JSX.Element;

// Estendi dayjs con il plugin isoWeek
dayjs.extend(isoWeek);
registerLocale("it", it);
registerLocale("en", en);

export interface ICustomDatePicker {
  date: Date | null;
  onChange?: (date: Date | null) => void;
  classNameInput?: string;
  disabled?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  onBlurInput?: () => void;
  showTimeSelect?: boolean;
  showTimeSelectOnly?: boolean;
  showIcon?: boolean;
  showYearDropdown?: boolean;
  color?: "secondary";
  dateFormat?: string | string[];
  showMonthDropdown?: boolean;
  minTime?: Date;
  maxTime?: Date;
  timeIntervals?: number;
  timeCaption?: string;
  onFocusInput?: () => void;
  label?: string;
  startOfWeek?: boolean;
  className?: string;
  maxDate?: Date;
  minDate?: Date;
}

const CustomDatePicker: FC<ICustomDatePicker> = ({
  date,
  onChange,
  showTimeSelect,
  showTimeSelectOnly,
  timeIntervals,
  timeCaption,
  showIcon = true,
  classNameInput = "",
  minTime,
  maxTime,
  color,
  placeholder = "gg/mm/aaaa",
  disabled = false,
  showYearDropdown = true,
  showMonthDropdown = true,
  isClearable = false,
  onBlurInput,
  onFocusInput,
  startOfWeek = false,
  label,
  dateFormat = "dd/MM/yyyy",
  className = "",
  maxDate,
  minDate,
}) => {
  // const [currentDate, setCurrentDate] = useState<Date | null>(initialDate);
  // useEffect(() => {
  //   onChange?.(currentDate);
  //   // eslint-disable-next-line
  // }, [currentDate]);

  const getClassDay = (day: Date) => {
    switch (true) {
      case day && dayjs(day!).isSame(dayjs(date)):
        return "calendar-custom-day-selected";
      case dayjs(day).format("DD-MM") === dayjs().format("DD-MM"):
        return "calendar-custom-day-today";
      default:
        return "calendar-custom-day";
    }
  };

  // const CustomInputDatePicker = forwardRef(({ value, onClick }: any, ref) => {
  //   return (
  //     <>
  //       <button
  //         className={`calendar-custom-input ${classNames({
  //           disabled,
  //         })} ${classNameInput}`}
  //         onClick={onClick}
  //         disabled={disabled}
  //         ref={ref as any}
  //       >
  //         <div className="d-flex align-items-center justify-content-between gap-3 w-100">
  //           <span
  //             className={`${objectToClassString({
  //               "data-label": value,
  //               "data-label-placeholder": !value,
  //               disabled,
  //             })}`}
  //           >
  //             {value || placeholder}
  //           </span>
  //           <div
  //             style={{
  //               width: 32,
  //               display: "flex",
  //               justifyContent: "center",
  //               alignItems: "center",
  //             }}
  //           >
  //             {isClearable && value ? (
  //               <CloseIcon color={"red.400"} fontSize={"0.8rem"} />
  //             ) : (
  //               <CalendarIcon color={"#cccccc"} fontSize={"1rem"} />
  //             )}
  //           </div>
  //         </div>
  //       </button>
  //     </>
  //   );
  // });

  return (
    <>
      <div
        className={`container__custom__input__datepicker ${
          color || ""
        } ${className}`}
      >
        {label && <span className="text-bold-14 label">{label}</span>}
        <TypedDatePicker
          showTimeSelect={showTimeSelect}
          showTimeSelectOnly={showTimeSelectOnly}
          onCalendarClose={() => {
            onBlurInput && onBlurInput();
          }}
          dateFormat={dateFormat}
          timeCaption={timeCaption}
          popperPlacement="bottom"
          timeIntervals={timeIntervals}
          minTime={minTime}
          maxTime={maxTime}
          icon={<CalendarDotsIcon />}
          onCalendarOpen={() => onFocusInput && onFocusInput()}
          selected={date}
          wrapperClassName={"calendar-wrapper"}
          calendarClassName="calendar-month"
          className="container-calendar"
          placeholderText={placeholder || `gg/mm/YYYY`}
          showIcon={showIcon}
          weekDayClassName={() => "calendar-week-day"}
          // customInput={<CustomInputDatePicker />}
          locale={"it"}
          renderCustomHeader={({
            date,
            changeYear,
            changeMonth,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <div className="container-calendar-header">
              <button
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
              >
                <CaretLeftIcon />
              </button>
              <MonthDatepicker
                date={date}
                changeMonth={changeMonth}
                changeYear={changeYear}
              />
              <button
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
              >
                <CaretRightIcon />
              </button>
            </div>
          )}
          showYearDropdown={showYearDropdown}
          showMonthDropdown={showMonthDropdown}
          maxDate={maxDate}
          minDate={minDate}
          isClearable={isClearable}
          dayClassName={(date) => getClassDay(date)}
          onChange={(date) =>
            startOfWeek
              ? onChange?.(dayjs(date).startOf("isoWeek").toDate())
              : onChange?.(date)
          }
        />
      </div>
    </>
  );
};

export default CustomDatePicker;
